import React from 'react';
import { el } from '../i18n/el';

/**
 * Two-step visual indicator for invoice extraction:
 *   1. Image analysis  (Azure OCR)
 *   2. Data extraction (GPT)
 *
 * Each step is shown as a checkbox-like circle that fills green when done.
 * A dashed connector animates while the next step is running.
 *
 * Status values per step: 'idle' | 'running' | 'done' | 'error'.
 */
function StepIcon({ status }) {
  if (status === 'done') {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md shadow-emerald-200">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-100 text-blue-700 ring-2 ring-blue-300">
        <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500 text-white shadow-md shadow-red-200">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v5a1 1 0 102 0V6zm-1 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      </span>
    );
  }
  return (
    <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-gray-300 bg-white text-gray-300">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
        <circle cx="10" cy="10" r="3" />
      </svg>
    </span>
  );
}

function statusLabel(status) {
  if (status === 'done') return el.dashStepDone;
  if (status === 'running') return el.dashStepRunning;
  if (status === 'error') return el.dashStepError;
  return el.dashStepWaiting;
}

function statusTone(status) {
  if (status === 'done') return 'text-emerald-600';
  if (status === 'running') return 'text-blue-600';
  if (status === 'error') return 'text-red-600';
  return 'text-gray-400';
}

export default function ExtractionStepper({ ocr = 'idle', gpt = 'idle', orientation = 'horizontal', compact = false }) {
  const isHorizontal = orientation === 'horizontal';

  const Connector = () => (
    <div
      className={
        isHorizontal
          ? 'flex-1 mx-2 h-0.5 relative overflow-hidden'
          : 'my-1 ml-4 w-0.5 h-6 relative overflow-hidden'
      }
      aria-hidden
    >
      <div
        className={`absolute inset-0 border-dashed ${
          isHorizontal ? 'border-t-2' : 'border-l-2'
        } ${gpt === 'running' ? 'border-blue-400 animate-pulse' : ocr === 'done' ? 'border-emerald-400' : 'border-gray-300'}`}
      />
    </div>
  );

  const Step = ({ status, label }) => (
    <div className={isHorizontal ? 'flex items-center gap-2 min-w-0' : 'flex items-start gap-3'}>
      <StepIcon status={status} />
      {!compact && (
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-800 truncate">{label}</div>
          <div className={`text-xs ${statusTone(status)}`}>{statusLabel(status)}</div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        isHorizontal
          ? 'flex items-center w-full'
          : 'flex flex-col'
      }
      role="status"
      aria-live="polite"
    >
      <Step status={ocr} label={el.dashStepImageAnalysis} />
      <Connector />
      <Step status={gpt} label={el.dashStepDataExtraction} />
    </div>
  );
}
